import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Group,
    Loader,
    Modal,
    NumberInput,
    Select,
    Stack,
    Text,
    Tooltip,
} from '@mantine/core';
import { Info } from '@phosphor-icons/react';

import {
    getEntityMasterFilterOptions,
    getEntityMasterTable,
} from '@api/entity-attributes';
import {
    createProductVendorMapping,
    createVendorMaterialMapping,
    listVendors,
} from '@api/vendors';
import { listVendorsForPicker } from '@api/work-order';
import useApp from '@providers/App/useApp';
import { readApiError } from '@utils/apiError';

/** Which side of the vendor's catalogue the mapping goes on. */
export type VendorMappingKind = 'product' | 'material';

interface AddVendorMappingModalProps {
    opened: boolean;
    onClose: () => void;
    /** Fired after a successful create so the grid can reload. */
    onCreated: () => void;
    /**
     * Product mappings say which manufacturer MAKES a product, priced in a currency.
     * Material mappings say which supplier SELLS a material, with a rate in the brand's
     * own currency unless another is picked (an import supplier's USD rate). One form,
     * because the two differ only in what is picked and what the number is called.
     */
    kind?: VendorMappingKind;
}

/** Currencies to offer when the merchant has recorded none of their own yet. */
const SUGGESTED_CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'CNY'];

const PRODUCT_PAGE_SIZE = 50;

/*
 * Left-aligned under its input. The theme caps every Select dropdown at 268px and Mantine
 * centres it, which in a full-width modal field reads as a dropdown shoved to the right.
 */
const DROPDOWN = { width: 'target', position: 'bottom-start' } as const;

const mergeCurrencies = (own: string[]): string[] => {
    const seen = new Set<string>();
    const options: string[] = [];

    for (const value of [...own, ...SUGGESTED_CURRENCIES]) {
        const key = value.trim().toUpperCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        options.push(key);
    }

    return options;
};

const AddVendorMappingModal: React.FC<AddVendorMappingModalProps> = ({
    opened,
    onClose,
    onCreated,
    kind = 'product',
}) => {
    const isMaterial = kind === 'material';
    // Only an admin may put a price on a mapping; the server refuses anyone else's.
    const { currentUser } = useApp();
    const isAdmin = currentUser?.role === 'ADMIN';
    const [productId, setProductId] = useState<string | null>(null);
    const [vendorId, setVendorId] = useState<string | null>(null);
    const [price, setPrice] = useState<string | number>('');
    const [currency, setCurrency] = useState<string | null>(null);

    const [productOptions, setProductOptions] = useState<
        { value: string; label: string }[]
    >([]);
    const [productSearch, setProductSearch] = useState('');
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [productsTruncated, setProductsTruncated] = useState(false);

    const [vendorOptions, setVendorOptions] = useState<
        { value: string; label: string }[]
    >([]);
    const [currencyOptions, setCurrencyOptions] =
        useState<string[]>(SUGGESTED_CURRENCIES);

    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const reset = () => {
        setProductId(null);
        setVendorId(null);
        setPrice('');
        setCurrency(null);
        setProductSearch('');
        setFieldErrors({});
        setSaveError(null);
        setSaving(false);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    /*
     * Products are searched server-side rather than loaded whole. The largest
     * merchant has thousands of them, and the master table endpoint already
     * paginates and searches by name or SKU — so this needs no picker endpoint of
     * its own, and the browser never holds the full catalogue.
     */
    useEffect(() => {
        if (!opened) return;

        let cancelled = false;
        setLoadingProducts(true);

        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            void getEntityMasterTable({
                entityType: isMaterial ? 'MATERIAL' : 'PRODUCT',
                page: 1,
                pageSize: PRODUCT_PAGE_SIZE,
                search: productSearch.trim() || undefined,
                // A retired product or material is not something a vendor can be mapped to.
                coreFilters: { isActive: ['true'] },
            })
                .then((result) => {
                    if (cancelled) return;
                    setProductOptions(
                        result.rows.map((row) => {
                            const id = String(row._entityKey ?? '');
                            const name = String(row.name ?? '');

                            return {
                                value: id,
                                label: name ? `${name} — ${id}` : id,
                            };
                        }),
                    );
                    setProductsTruncated(result.total > PRODUCT_PAGE_SIZE);
                })
                .catch(() => {
                    if (!cancelled) setProductOptions([]);
                })
                .finally(() => {
                    if (!cancelled) setLoadingProducts(false);
                });
        }, 300);

        return () => {
            cancelled = true;
            if (searchTimer.current) clearTimeout(searchTimer.current);
        };
    }, [opened, productSearch, isMaterial]);

    useEffect(() => {
        if (!opened) return;

        // Manufacturers make products; any purchase vendor may supply a material.
        void (isMaterial ? listVendors() : listVendorsForPicker())
            .then((vendors) =>
                setVendorOptions(
                    vendors.map((v) => ({
                        value: v.vendorId,
                        label:
                            v.name && v.name !== v.vendorId
                                ? `${v.name} — ${v.vendorId}`
                                : v.vendorId,
                    })),
                ),
            )
            .catch(() => setVendorOptions([]));

        void getEntityMasterFilterOptions(isMaterial ? 'VENDOR_MATERIAL_MAPPING' : 'VENDOR_PRODUCT_MAPPING')
            .then((options) =>
                setCurrencyOptions(mergeCurrencies(options.currency ?? [])),
            )
            .catch(() => setCurrencyOptions(SUGGESTED_CURRENCIES));
    }, [opened, isMaterial]);

    const validate = (): boolean => {
        const errors: Record<string, string> = {};
        if (!productId) errors.productId = isMaterial ? 'Choose a material' : 'Choose a product';
        if (!vendorId) errors.vendorId = 'Choose a vendor';
        // A price without a currency is a number nobody can act on.
        if (!isMaterial && price !== '' && !currency) {
            errors.currency = 'Choose the currency this price is in';
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate() || !productId || !vendorId) return;

        setSaving(true);
        setSaveError(null);
        try {
            if (isMaterial) {
                await createVendorMaterialMapping({
                    materialId: productId,
                    vendorId,
                    cost: price === '' ? undefined : Number(price),
                    currency: currency ?? undefined,
                });
            } else {
                await createProductVendorMapping({
                    productId,
                    vendorId,
                    price: price === '' ? undefined : Number(price),
                    currency: currency ?? undefined,
                });
            }
            onCreated();
            handleClose();
        } catch (err) {
            setSaveError(
                readApiError(err, 'Could not add the mapping. Please try again.'),
            );
            setSaving(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={isMaterial ? 'Add vendor material mapping' : 'Add vendor product mapping'}
            size="md"
            centered
        >
            <Stack gap="md">
                <Alert icon={<Info size={16} />} color="blue" variant="light">
                    <Text size="xs">
                        Says that this vendor supplies this {isMaterial ? 'material' : 'product'}.
                        The {isMaterial ? 'rate' : 'price'} is optional — record the mapping now
                        and fill it in once it is agreed.
                    </Text>
                </Alert>

                <Select
                    label={isMaterial ? 'Material' : 'Product'}
                    description={
                        productsTruncated
                            ? 'Showing the first 50 matches — type to narrow it down'
                            : isMaterial
                              ? 'Search by name or code'
                              : 'Search by name or SKU'
                    }
                    placeholder={
                        isMaterial
                            ? 'Start typing a material name or code'
                            : 'Start typing a product name or SKU'
                    }
                    data={productOptions}
                    value={productId}
                    onChange={setProductId}
                    searchValue={productSearch}
                    onSearchChange={setProductSearch}
                    error={fieldErrors.productId}
                    rightSection={loadingProducts ? <Loader size={14} /> : undefined}
                    nothingFoundMessage={
                        loadingProducts
                            ? 'Searching…'
                            : isMaterial
                              ? 'No matching material'
                              : 'No matching product'
                    }
                    comboboxProps={DROPDOWN}
                    withAsterisk
                    searchable
                />

                <Select
                    label="Vendor"
                    placeholder="Choose a vendor"
                    data={vendorOptions}
                    value={vendorId}
                    onChange={setVendorId}
                    error={fieldErrors.vendorId}
                    withAsterisk
                    searchable
                    comboboxProps={DROPDOWN}
                />

                <Group grow align="flex-end">
                    <Tooltip label="Admins only" disabled={isAdmin} withArrow>
                        <div>
                            <NumberInput
                                label={isMaterial ? 'Agreed rate' : 'Price'}
                                description={
                                    isMaterial
                                        ? 'Per unit. Optional; no currency means your own.'
                                        : 'What this vendor charges. Optional.'
                                }
                                placeholder="49.50"
                                value={price}
                                onChange={(value) =>
                                    setPrice(value === '' ? '' : Number(value))
                                }
                                min={0}
                                decimalScale={2}
                                allowNegative={false}
                                disabled={!isAdmin}
                            />
                        </div>
                    </Tooltip>
                    <Select
                        label="Currency"
                        placeholder={isMaterial ? 'Your own' : 'Choose'}
                        data={currencyOptions}
                        value={currency}
                        onChange={setCurrency}
                        error={fieldErrors.currency}
                        clearable
                        searchable
                    />
                </Group>

                {saveError && (
                    <Alert color="red" variant="light">
                        <Text size="xs">{saveError}</Text>
                    </Alert>
                )}

                <Group justify="flex-end" gap="sm">
                    <Button variant="default" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button loading={saving} onClick={handleSubmit}>
                        Add mapping
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default AddVendorMappingModal;
